/**
 * The play layer: mounts a game iframe, runs the bridge host, and owns
 * the run's score lifecycle (initials → submitScore → rank toast).
 *
 * One launch = one iframe. BACK tears the whole thing down — games keep
 * their own retry loops inside the frame, and every finished run fires
 * a fresh `gameover`, so multiple submissions per launch are expected.
 */

import { createCartridgeHost, type CartridgeHost } from '@arcraz/game-bridge';
import { submitScore } from '../api/client.js';
import { gameUrl, type CatalogEntry } from '../catalog.js';
import { track } from '../runtime/analytics.js';
import { getOrCreateDeviceId } from '../runtime/identity.js';
import { runInitialsEntry } from './initials.js';
import { recordPlay } from './plays.js';
import { pushToast } from './toast.js';
import { getCabinetMuted, onCabinetMuteChange, setCabinetMuted } from './mute.js';

/** Same rhythm as the in-game (bridge) double-ESC. */
const EXIT_DOUBLE_TAP_MS = 1000;

function newSeed(): string {
  // Arcade-mode runs are non-deterministic in the remaster; the seed
  // column still gets a unique run tag (schema requires 1..128 chars).
  return `remaster-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface PlaySession {
  close(): void;
}

export function launchGame(entry: CatalogEntry, opts: { onClose: () => void }): PlaySession {
  const layer = document.getElementById('play-layer');
  const frameSlot = document.getElementById('play-frame-slot');
  const titleEl = document.getElementById('play-title');
  const scoreEl = document.getElementById('play-score');
  const backBtn = document.getElementById('play-back');
  if (
    !(layer instanceof HTMLElement) ||
    !(frameSlot instanceof HTMLElement) ||
    !(titleEl instanceof HTMLElement) ||
    !(scoreEl instanceof HTMLElement) ||
    !(backBtn instanceof HTMLButtonElement)
  ) {
    throw new Error('play layer DOM missing — index.html is broken');
  }
  // Hoisted `close()` below can't see the instanceof narrowing — alias
  // the proven nodes so the compiler carries the types into the closure.
  const layerEl: HTMLElement = layer;
  const backBtnEl: HTMLButtonElement = backBtn;

  titleEl.textContent = entry.title;
  titleEl.style.color = entry.accent;
  scoreEl.textContent = entry.scoreless ? '' : '000000';

  const iframe = document.createElement('iframe');
  iframe.className = 'play-frame';
  iframe.src = gameUrl(entry);
  // pointer-lock is load-bearing: bunker-84 is unplayable without it.
  iframe.allow = 'autoplay; fullscreen; pointer-lock';
  iframe.title = entry.title;
  frameSlot.appendChild(iframe);

  const seed = newSeed();
  const startedAt = performance.now();
  let submitting = false;
  let playRecorded = false;

  const host: CartridgeHost = createCartridgeHost(iframe, {
    onReady: () => {
      host.init({ muted: getCabinetMuted(), seed });
      track('cartridge_started', { cartridgeId: entry.id, remastered: true });
    },
    onStarted: () => {
      // The play (and its free-play meter tick) burns HERE — the player
      // dismissed the start overlay and committed to a run. Launching
      // and backing out from the start screen costs nothing. Guarded to
      // once per launch: in-frame retries re-fire `started`, and a
      // retry is not a new coin.
      if (playRecorded) return;
      playRecorded = true;
      recordPlay(entry.id);
    },
    onScore: (score) => {
      if (!entry.scoreless) {
        scoreEl.textContent = String(Math.floor(score)).padStart(6, '0');
      }
    },
    onGameOver: (result) => {
      void handleGameOver(result.score, result.won);
    },
    onExit: () => close(),
    onMuted: (muted) => setCabinetMuted(muted),
  });

  const offMute = onCabinetMuteChange((muted) => host.setMuted(muted));

  async function handleGameOver(score: number, won: boolean): Promise<void> {
    track('cartridge_run_ended', {
      cartridgeId: entry.id,
      score,
      won,
      durationMs: Math.round(performance.now() - startedAt),
    });
    if (entry.scoreless || score <= 0 || submitting) return;
    submitting = true;
    try {
      const initials = await runInitialsEntry(score);
      if (initials === null) return; // player skipped the board
      const response = await submitScore({
        cartridgeId: entry.id,
        cartridgeVersion: entry.validatorVersion,
        score: Math.floor(score),
        initials,
        seed,
        inputFrames: [],
        deviceId: getOrCreateDeviceId(),
      });
      pushToast(`${entry.title} — RANK #${response.rank}`, 'success');
      track('score_submitted', {
        cartridgeId: entry.id,
        score,
        rank: response.rank,
      });
    } catch {
      pushToast('SCORE LOST IN TRANSIT — THE SCOREBOARD WILL RISE AGAIN', 'error');
    } finally {
      submitting = false;
    }
  }

  let closed = false;
  function close(): void {
    // Guarded: the bridge onExit and the parent ESC handler can race
    // (whichever window held focus got the keys) — close once.
    if (closed) return;
    closed = true;
    offMute();
    window.removeEventListener('keydown', onParentKeydown);
    host.dispose();
    iframe.remove();
    layerEl.hidden = true;
    document.body.dataset.mode = 'attract';
    backBtnEl.removeEventListener('click', close);
    track('cartridge_exited', {
      cartridgeId: entry.id,
      durationMs: Math.round(performance.now() - startedAt),
    });
    opts.onClose();
  }
  backBtn.addEventListener('click', close);

  // Parent-side double-ESC. The bridge installs the same hatch inside
  // the game's window, but keyboard focus doesn't reach the iframe
  // until the player clicks into it — on the loading/start screen every
  // keypress lands HERE. Without this, a player who launched by mistake
  // can't leave without starting a run.
  let lastEscAt = 0;
  function onParentKeydown(e: KeyboardEvent): void {
    if (e.code !== 'Escape') return;
    const now = Date.now();
    if (now - lastEscAt < EXIT_DOUBLE_TAP_MS) {
      close();
    } else {
      lastEscAt = now;
    }
  }
  window.addEventListener('keydown', onParentKeydown);

  layer.hidden = false;
  document.body.dataset.mode = 'playing';
  // Focus once the document inside actually exists — focusing the empty
  // frame pre-load doesn't stick, which is how ESC ended up dead on
  // start screens (keys stayed in the parent, see handler above).
  iframe.addEventListener('load', () => iframe.focus());
  iframe.focus();

  return { close };
}
